// packages/ui-kit/src/primitives/Tabs.tsx
// C3F v18 — reference: design/system/components/navigation/Tabs.{jsx,prompt.md}
import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "../lib/cn.js";

interface Item {
  id: string;
  label: string;
  href?: string;
  /** Numeric counter rendered as a mono pill. Active tab pairs it with the
   *  opaque accent-soft/accent-text duo; inactive falls back to bg-subtle
   *  (veils never host text — build.md). */
  count?: number;
  /** Free-form adornment (legacy call sites); prefer `count` for numbers. */
  badge?: ReactNode;
}

interface Props {
  items: Item[];
  active: string;
  onChange?: (id: string) => void;
  /** The active underline is the only colored filet of the system (--accent). */
  variant?: "underline" | "pill";
  className?: string;
}

function Count({ value, isActive }: { value: number; isActive: boolean }) {
  return (
    <span
      className={cn(
        "rounded-pill px-[7px] py-px font-mono text-mono whitespace-nowrap",
        isActive ? "bg-accent-soft text-accent-text" : "bg-subtle text-fg-muted",
      )}
    >
      {value}
    </span>
  );
}

export function Tabs({ items, active, onChange, variant = "underline", className = "" }: Props) {
  const isPill = variant === "pill";

  const itemCls = (isActive: boolean) =>
    cn(
      "inline-flex min-h-touch items-center gap-2 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus",
      isPill
        ? cn(
            "rounded-pill px-3.5",
            isActive ? "bg-subtle text-fg-default" : "text-fg-muted hover:bg-hover",
          )
        : cn(
            // Underline: the 2px accent filet under the active tab. Hover moves
            // TEXT, never borders (build.md: hover never darkens borders).
            "border-b-2 pb-2.5",
            isActive
              ? "border-accent text-fg-default"
              : "border-transparent text-fg-muted hover:text-fg-default",
          ),
    );

  return (
    <nav
      role="tablist"
      className={cn(
        isPill
          ? "flex flex-wrap items-center gap-1.5"
          : "flex items-center gap-6 -mx-4 px-4 border-b border-border-default overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {items.map((it) => {
        const isActive = it.id === active;
        const content = (
          <>
            {it.label}
            {it.count !== undefined && <Count value={it.count} isActive={isActive} />}
            {it.badge}
          </>
        );
        return it.href ? (
          <Link
            key={it.id}
            to={it.href as never}
            aria-current={isActive ? "page" : undefined}
            className={itemCls(isActive)}
          >
            {content}
          </Link>
        ) : (
          <button
            key={it.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={itemCls(isActive)}
            onClick={() => onChange?.(it.id)}
          >
            {content}
          </button>
        );
      })}
    </nav>
  );
}
